import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useUser } from 'global/UserContext';
import { apiOrchestrator } from 'services/apiService';

export interface Company {
  uuid: string;
  name: string;
  email: string;
  organization_uuid: string;
  created_at: string;
  updated_at: string;
}

interface CompanyFormData {
  name: string;
  email: string;
  companyPosition?: string;
}

interface UserCompany {
  name: string;
  uuid: string;
  email: string;
  social_networks: any[];
}

interface UserData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  whatsapp_number: string;
  user_to_organization: any[];
  user_to_teams: any[];
  social_networks: any[];
  person_id: string;
  is_advisor: boolean;
  company: UserCompany | null;
  company_role: string;
}

const schema = yup.object().shape({
  name: yup.string().required('Nome da empresa é obrigatório'),
  email: yup.string().email('Email inválido').required('Email da empresa é obrigatório'),
  companyPosition: yup.string().when('selectedCompany', {
    is: (selectedCompany: Company | null) => !!selectedCompany,
    then: () => yup.string().required('Cargo na empresa é obrigatório'),
    otherwise: () => yup.string().optional(),
  }),
});

export const useCompanyForm = () => {
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const { user } = useUser();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CompanyFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      companyPosition: '',
    },
  });

  // Carrega os dados do usuário quando o componente monta
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        // Busca os dados do usuário
        const response = await apiOrchestrator.get(`/auth/me/${user.id}`);
        const userData: UserData = response.data;

        // Se o usuário já está vinculado a uma empresa
        if (userData.company) {
          // Atualiza o estado e preenche os campos
          setSelectedCompany({
            uuid: userData.company.uuid,
            name: userData.company.name,
            email: userData.company.email,
            organization_uuid: '',
            created_at: '',
            updated_at: '',
          });
          setValue('name', userData.company.name);
          setValue('email', userData.company.email);
          setValue('companyPosition', userData.company_role || '');
        }
      } catch (error) {
        console.error('Erro ao carregar dados do usuário:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user?.id, setValue]);

  // Função para buscar empresas por nome
  const searchCompanies = async (name: string) => {
    if (!name.trim()) {
      setCompanies([]);
      return;
    }

    try {
      const response = await apiOrchestrator.get(`/companies/?name=${name}`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Erro ao buscar empresas:', error);
      setCompanies([]);
    }
  };

  // Função para selecionar uma empresa existente
  const handleCompanySelect = async (company: Company | null) => {
    if (!company) {
      setSelectedCompany(null);
      setValue('name', '');
      setValue('email', '');
      setValue('companyPosition', '');
      return;
    }
    try {
      setLoading(true);
      setSelectedCompany(company);
      setValue('name', company.name);
      setValue('email', company.email);
      setValue('companyPosition', ''); // Limpa o cargo ao selecionar uma nova empresa
    } catch (error) {
      console.error('Erro ao selecionar empresa:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função para limpar a seleção
  const handleClearSelection = () => {
    setSelectedCompany(null);
    setValue('name', '');
    setValue('email', '');
    setValue('companyPosition', '');
  };

  // Função para criar uma nova empresa
  const onSubmit = async (data: CompanyFormData) => {
    if (!user?.id) {
      console.error('Usuário não autenticado');
      return;
    }

    try {
      setLoading(true);

      // Se já existe uma empresa selecionada, apenas atualiza o cargo
      if (selectedCompany) {
        // Se não houver cargo, não faz nada
        if (!data.companyPosition?.trim()) {
          return;
        }

        await apiOrchestrator.put(
          `/users-person/update-company/${user.id}?company_uuid=${selectedCompany.uuid}&company_role=${data.companyPosition}`
        );
        return;
      }

      // Cria a empresa
      const companyResponse = await apiOrchestrator.post('/companies/', {
        name: data.name,
        email: data.email,
      });

      const newCompany = companyResponse.data;

      // Vincula o usuário à empresa apenas se o cargo foi fornecido
      if (data.companyPosition?.trim()) {
        await apiOrchestrator.put(
          `/users-person/update-company/${user.id}?company_uuid=${newCompany.uuid}&company_role=${data.companyPosition}`
        );
      }

      setSelectedCompany(newCompany);
    } catch (error) {
      console.error('Erro ao criar/atualizar empresa:', error);
      throw error; // Propaga o erro para o formulário
    } finally {
      setLoading(false);
    }
  };

  return {
    control,
    handleSubmit,
    setValue,
    watch,
    errors,
    loading,
    selectedCompany,
    companies,
    searchCompanies,
    handleCompanySelect,
    handleClearSelection,
    onSubmit,
  };
};
